import { getFormatter, isDefined } from '@eih-netbroker/common';
import { TooltipProps } from '@mui/material';
import { memo, useCallback, useMemo, useRef } from 'react';
import { useLocale } from '../../../../../context/NbLocalizationContext';
import { OverflowTooltip } from '../../../../../shared/components/tooltip/OverflowTooltip';
import { cssWhen } from '../../../../../shared/utils/themeUtils';
import classes from './OrderFormPricePresetSelector.module.scss';

type OrderFormPricePresetSelector = {
  id: string;
  label: string;
  value?: number;
};

const unknownValue = '-';
const tooltipSlotProps: TooltipProps['slotProps'] = {
  popper: { modifiers: [{ name: 'offset', options: { offset: [0, -14] } }] },
};

type OrderFormPricePresetSelectorProps = {
  currentValue?: number;
  onChange: (value: number) => void;
  bestBid?: number;
  bestAsk?: number;
  midPrice?: number;
  disabled?: boolean;
  className?: string;
  readOnly?: boolean;
};

export const PricePresetSelector = memo(
  ({
    currentValue,
    onChange,
    bestBid,
    bestAsk,
    midPrice,
    disabled,
    className,
    readOnly,
  }: OrderFormPricePresetSelectorProps) => {
    const { translation } = useLocale();
    const { formatNumber } = getFormatter();
    const containerRef = useRef<HTMLDivElement>(null);

    const presets = useMemo<Array<OrderFormPricePresetSelector>>(
      () => [
        { id: 'best-bid', label: translation.orderModal.bestBid, value: bestBid },
        { id: 'mid-price', label: translation.orderModal.midPrice, value: midPrice },
        { id: 'best-ask', label: translation.orderModal.bestAsk, value: bestAsk },
      ],
      [translation, bestBid, midPrice, bestAsk],
    );

    const handleClick = useCallback(
      (preset: OrderFormPricePresetSelector, isSelected: boolean): void => {
        if (!isDefined(preset.value) || readOnly || isSelected) {
          return;
        }
        onChange(preset.value);
      },
      [onChange, readOnly],
    );

    return (
      <div
        ref={containerRef}
        className={cssWhen(classes.container, className)}
      >
        {presets.map(preset => {
          const hasValue = isDefined(preset.value);
          const isSelected = hasValue && currentValue !== undefined && currentValue === preset.value;

          return (
            <button
              key={preset.id}
              type="button"
              className={cssWhen(classes.item, [isSelected, classes.itemSelected])}
              onClick={() => handleClick(preset, isSelected)}
              disabled={disabled || !hasValue}
              data-testid={`order-form-price-preset-${preset.id}`}
            >
              <OverflowTooltip
                title={preset.label}
                parentContainerRef={containerRef}
                placement="top"
                slotProps={tooltipSlotProps}
              >
                <span className={classes.itemLabel}>{preset.label}</span>
              </OverflowTooltip>
              <span className={classes.itemValue}>{hasValue ? formatNumber(preset.value!) : unknownValue}</span>
            </button>
          );
        })}
      </div>
    );
  },
);
